import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { VisualEditorProps } from '../../types/visual-editor';
import { JsonAnalyzer } from '../../utils/jsonAnalyzer';
import { FieldRenderer } from './FieldComponents';
import { Eye } from 'lucide-react';
import { ImageService } from '../../services';

interface VisualEditorWithImageProps extends VisualEditorProps {
  imageService?: ImageService;
}

export const VisualEditor: React.FC<VisualEditorWithImageProps> = ({ data, onChange, loading, imageService }) => {
  // Usar ref para mantener referencia actualizada sin causar re-renders
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  
  // Actualizar refs en cada render
  useEffect(() => {
    dataRef.current = data;
    onChangeRef.current = onChange;
  });

  // Memoizar fields para evitar re-cálculo innecesario
  const fields = useMemo(() => {
    if (!data) return [];
    return JsonAnalyzer.analyzeJson(data);
  }, [data]);

  // Callbacks estables que usan refs
  const handleFieldChange = useCallback((path: string, value: any) => {
    const updatedData = JsonAnalyzer.updateValue(dataRef.current, path, value);
    onChangeRef.current(updatedData);
  }, []); // Sin dependencias - siempre usa la ref actualizada

  const handleAddArrayItem = useCallback((path: string) => {
    const arrayPath = path.split('.');
    const arrayKey = arrayPath[arrayPath.length - 1];
    
    // Encontrar el tipo de item del array
    const field = fields.find(f => f.key === arrayKey);
    if (field && field.arrayItemType) {
      const newItem = field.arrayItemType === 'object' 
        ? (field.arrayItemConfig ? { ...field.arrayItemConfig.value } : {})
        : field.arrayItemType === 'number' 
        ? 0 
        : field.arrayItemType === 'boolean' 
        ? false 
        : '';
      
      const updatedData = JsonAnalyzer.addArrayItem(dataRef.current, path, newItem);
      onChangeRef.current(updatedData);
    }
  }, [fields]); // Solo depende de fields

  const handleRemoveArrayItem = useCallback((path: string, index: number) => {
    const updatedData = JsonAnalyzer.removeArrayItem(dataRef.current, path, index);
    onChangeRef.current(updatedData);
  }, []); // Sin dependencias

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="visual-editor-empty">
        <div className="empty-state">
          <Eye size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3>No hay datos para editar</h3>
          <p>Selecciona un archivo JSON para comenzar a editarlo visualmente</p>
        </div>
      </div>
    );
  }

  return (
    <div className="visual-editor">
      <div className="visual-editor-header">
        <div className="editor-title">
          <Eye size={20} />
          <h3>Editor Visual</h3>
        </div>
      </div>

      <div className="visual-editor-content">
        <div className="fields-container">
          {fields.map((field) => (
            <FieldRenderer
              key={field.key}
              config={field}
              onChange={handleFieldChange}
              onAddArrayItem={handleAddArrayItem}
              onRemoveArrayItem={handleRemoveArrayItem}
              imageService={imageService}
            />
          ))}
        </div>

      </div>
    </div>
  );
};
